import { useState } from "react";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loadable, Tabbar, TabPanel, useTabs, useLoad, fmt, Status, ActionButton, ActionDialog, can, deText } from "@/components/control/kit";
import { V3Page, V3Aside, Compare, Field, V3Actions, FilterEmpty } from "@/components/control/v3";

const STATE = { ausstehend: "Redaktionelle Prüfung ausstehend", "übernommen": "Übernommen", verworfen: "Verworfen" };

function NewReview({ onDone }) {
  const content = useLoad("/content?status=all");
  const acts = useLoad("/manage/activities");
  if (!content.data || !acts.data) return null;
  const targets = [...content.data.items.map((i) => ({ value: `content:${i.id}`, label: `Inhalt · ${deText(i.title)}` })),
    ...acts.data.activities.map((a) => ({ value: `activity:${a.id}`, label: `Aktivität · ${deText(a.title_de || a.title)}` }))];
  return (
    <ActionButton label="Vorschlag erfassen" icon={Plus} variant="default" testId="create-text-review" submitLabel="Zur Prüfung vorlegen" onDone={onDone}
      description="Der Originaltext wird aus dem gewählten Eintrag übernommen. Eine andere Person mit Freigaberecht entscheidet."
      fields={[{ name: "target", label: "Text", type: "select", required: true, options: targets },
        { name: "proposal", label: "Einfachere Formulierung", type: "textarea", required: true },
        { name: "question", label: "Prüffrage", default: "Ist der nächste Schritt verständlich?" }]}
      onSubmit={async ({ target, ...v }) => {
        const [target_type, target_id] = target.split(":");
        await api.post("/text-reviews", { ...v, target_type, target_id });
        return "Vorschlag zur Prüfung vorgelegt.";
      }} />
  );
}

function ReviewView({ r, reload, onBack }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const own = r.created_by?.id === user?.id;
  const mayDecide = r.status === "ausstehend" && can(user, "content:approve") && !own;
  return (
    <div className="space-y-2.5" data-testid={`text-review-${r.id}`}>
      <Compare before={r.original} after={r.proposal} testId="text-review-compare" />
      <Field label="Prüffrage">{r.question}</Field>
      <Field label="Status" testId="text-review-status">{STATE[r.status]}{r.decided_by ? ` · ${r.decided_by.name}` : ""}</Field>
      <Field label="Eintrag">{r.target_title} · vorgeschlagen von {r.created_by?.name}</Field>
      {r.status === "ausstehend" && own && <p className="text-sm text-zinc-600" data-testid="text-review-own-hint">Eigener Vorschlag: Die redaktionelle Prüfung übernimmt eine andere Person mit Freigaberecht.</p>}
      <V3Actions primary={mayDecide ? "Textänderung prüfen" : null} onPrimary={() => setOpen(true)} secondary="Zurück" onSecondary={onBack} testId="text-review" />
      <ActionDialog open={open} onOpenChange={setOpen} title="Textänderung prüfen" testId="text-review-decide-dialog" submitLabel="Entscheidung speichern" onDone={reload}
        description="Die Entscheidung ändert den Text nicht automatisch; die Übernahme erfolgt im jeweiligen Editor."
        fields={[{ name: "decision", label: "Entscheidung", type: "select", required: true,
          options: [{ value: "übernommen", label: "Formulierung übernehmen" }, { value: "verworfen", label: "Verwerfen" }] },
          { name: "reason", label: "Begründung", type: "textarea", required: true }]}
        onSubmit={async (v) => { const { data } = await api.post(`/text-reviews/${r.id}/decide`, v); return data.note; }} />
    </div>
  );
}

export default function TextReviews() {
  const { user } = useAuth();
  const [status, setStatus] = useState("ausstehend");
  const [selected, setSelected] = useState(null);
  const tabs = useTabs([{ value: "ausstehend", label: "Ausstehend" }, { value: "all", label: "Alle" }], status);
  const st = useLoad(`/text-reviews?status=${status}`);
  const current = st.data?.reviews.find((r) => r.id === selected);
  return (
    <V3Page crumb="Inhalte & Freigaben" title="Text auf einfache Verständlichkeit prüfen" subtitle="Prüfe eine einfachere Formulierung im Kontext des tatsächlichen Texts."
      aside={<V3Aside subjectTitle="Ausgewählter Vorgang" subject={current?.original || "Kein Text ausgewählt"} />} testId="text-reviews-page">
      {!current && (
        <div className="flex flex-wrap items-start justify-between gap-2">
          <Tabbar tabs={tabs} onChange={setStatus} testId="text-review-tabs" />
          {can(user, "content:manage") && <NewReview onDone={st.reload} />}
        </div>
      )}
      <TabPanel tabs={tabs} testId="text-review-panel">
        <Loadable state={st}>
          {(d) => (current ? <ReviewView r={current} reload={st.reload} onBack={() => setSelected(null)} />
            : d.reviews.length === 0 ? <FilterEmpty filters={[["Status", tabs.items[tabs.index].label], ["Bereich", "Textprüfungen"]]}
              onReset={status !== "all" ? () => setStatus("all") : null} /> : (
              <ul className="space-y-2.5" data-testid="text-reviews-list">
                {d.reviews.map((r) => (
                  <li key={r.id}>
                    <button type="button" onClick={() => setSelected(r.id)} data-testid={`text-review-open-${r.id}`}
                      className="flex w-full flex-wrap items-center justify-between gap-2 rounded-xl border border-[#E6E1F0] bg-white px-4 py-3 text-left hover:border-brand-magenta">
                      <span><span className="block font-semibold text-brand-purple">{r.target_title}</span><span className="text-sm text-zinc-600">{r.proposal} · {fmt(r.created_at)}</span></span>
                      <Status value={r.status} />
                    </button>
                  </li>
                ))}
              </ul>
            ))}
        </Loadable>
      </TabPanel>
    </V3Page>
  );
}
